import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
  ActionIcon,
  Anchor,
  Box,
  Button,
  Container,
  Divider,
  Grid,
  Group,
  Loader,
  PasswordInput,
  Stack,
  Text,
  TextInput,
  Tooltip,
  UnstyledButton,
} from '@mantine/core';
import {
  IconLock,
  IconMail,
  IconShoppingCart,
  IconSparkles,
  IconToolsKitchen2,
} from '@tabler/icons-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useMediaQuery } from '@mantine/hooks';
import { usePostHog } from '@posthog/react';
import { useAuth, AuthProvider } from '../hooks/useAuth';
import { usePageTitle } from '../hooks/usePageTitle';
import { heroFontFamily } from '../theme';
import { AnalyticsEvent } from '../utils/analytics';
import { deviceHasSignedIn } from '../utils/authDeviceHistory';
import { BrandWordmark } from '../components/UI/BrandWordmark';
import { trackSignUpReddit } from '../utils/redditPixel';
import { trackSignUpMeta } from '../utils/metaPixel';

const oauthProviders: Array<{
  provider: AuthProvider;
  name: string;
  icon: React.ReactNode;
  collapsed?: boolean;
}> = [
  {
    provider: 'google',
    name: 'Google',
    icon: (
      <svg width="18" height="18" viewBox="0 0 48 48" style={{ display: 'block' }}>
        <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"></path>
        <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"></path>
        <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"></path>
        <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"></path>
        <path fill="none" d="M0 0h48v48H0z"></path>
      </svg>
    ),
  },
  {
    provider: 'facebook',
    name: 'Facebook',
    icon: (
      <svg width="17" height="17" viewBox="0 0 24 24" fill="#1877F2">
        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
      </svg>
    ),
  },
  {
    provider: 'twitter',
    name: 'X',
    collapsed: true,
    icon: (
      <svg width="15" height="15" viewBox="0.254 0.25 500 451.954" fill="currentColor">
        <path d="M394.033.25h76.67L303.202 191.693l197.052 260.511h-154.29L225.118 294.205 86.844 452.204H10.127l179.16-204.77L.254.25H158.46l109.234 144.417zm-26.908 406.063h42.483L135.377 43.73h-45.59z"/>
      </svg>
    ),
  },
  {
    provider: 'yahoo',
    name: 'Yahoo',
    collapsed: true,
    icon: (
      <svg width="16" height="16" viewBox="0 0 3386.34 3010.5" fill="currentColor">
        <path d="M0 732.88h645.84l376.07 962.1 380.96-962.1h628.76l-946.8 2277.62H451.98l259.19-603.53L.02 732.88zm2763.84 768.75h-704.26L2684.65 0l701.69.03-622.5 1501.6zm-519.78 143.72c216.09 0 391.25 175.17 391.25 391.22 0 216.06-175.16 391.23-391.25 391.23-216.06 0-391.19-175.17-391.19-391.23 0-216.05 175.16-391.22 391.19-391.22z" fill="#5f01d1"/>
      </svg>
    ),
  },
];

// Every provider mark renders inside the same fixed box so the logos sit
// optically equal and identically spaced from their labels. The per-logo
// pixel sizes above compensate for glyph density: Google's airy multicolor G
// is the 18px reference, Facebook's solid circle drops to 17, and the solid
// X and Yahoo glyphs to 15/16.
const ProviderLogo = ({ children }: { children: React.ReactNode }) => (
  <Box
    style={{
      width: 20,
      height: 20,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
    }}
  >
    {children}
  </Box>
);

// The brand panel restates the landing pillars (personalization, shopping,
// taste) beside the form, so the CTA's promise survives to the moment of
// commitment without crowding the actions.
const valueProps = [
  { icon: IconToolsKitchen2, text: 'Dinners planned around your diet, schedule, and tastes' },
  { icon: IconShoppingCart, text: 'Grocery lists done for you — Kroger, Instacart, or any store' },
  { icon: IconSparkles, text: 'Swap any meal — it learns what you love' },
];

// Once per page load — see the redirect-signup tracking effect below.
let redirectSignupTracked = false;

// Return-path entry persisted across the OAuth redirect round-trip: router
// state dies with the page reload, sessionStorage doesn't. Cleared when the
// signed-in redirect consumes it.
const RETURN_KEY = 'myl_auth_return';
// Set (with the provider name) when a provider redirect leaves this page;
// read on the way back so the return shows a "signing you in" state instead
// of flashing the untouched form while Firebase settles the result — which
// reads as a failed sign-in.
const REDIRECT_PENDING_KEY = 'myl_auth_redirect_pending';
// Mirrors useAuth: localhost signs in via popup (no page leave), so the
// pending-redirect flag must not be set there — the popup resolves in place.
const usesPopupAuth =
  window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
type AuthReturn = { from?: string; ctaLabel?: string };

const readStoredReturn = (): AuthReturn => {
  try {
    return JSON.parse(sessionStorage.getItem(RETURN_KEY) ?? '{}');
  } catch {
    return {};
  }
};

// Errors render inside a fixed-height slot so their appearance never grows
// the form — the vertically-centered grid re-centers on any height change
// and the whole page shifts (same trick as the more-options reveal). The
// interior is sized for the two-line worst case of the auth error copy at
// the form's width, but negative vertical margins let it overlap the
// surrounding Stack gaps so the empty slot reads as normal spacing rather
// than a hole; the reserved height itself never changes, so the zero-shift
// guarantee holds with or without an error showing.
const FormErrorSlot = ({ error, mih = 38, my = -10 }: { error: string; mih?: number; my?: number }) => (
  <Box mih={mih} my={my} style={{ display: 'flex', alignItems: 'center' }}>
    {error && (
      <Text size="xs" c="red" lh={1.45}>
        {error}
      </Text>
    )}
  </Box>
);

interface AuthPageProps {
  mode: 'signup' | 'signin';
}

/**
 * The dedicated auth page, rendered at /signup and /signin. One page, two
 * framings: the route carries the referring intent (marketing CTAs navigate
 * to /signup, the header's plain "Sign in" to /signin), each framing
 * cross-links the other, and the email form defaults to the route's mode.
 * Successful auth returns to the referring page via router state, persisted
 * in sessionStorage so the OAuth redirect round-trip survives the reload.
 */
export const AuthPage = ({ mode }: AuthPageProps) => {
  const isSignupPage = mode === 'signup';
  usePageTitle(
    isSignupPage
      ? 'Create your free account - Meals You Love'
      : 'Sign in - Meals You Love'
  );

  const navigate = useNavigate();
  const location = useLocation();
  const posthog = usePostHog();
  const {
    user,
    loading: authLoading,
    signInWithProvider,
    signInWithEmail,
    signUpWithEmail,
    resetPassword,
    redirectResult,
    authError,
  } = useAuth();

  // Referring context: router state on a same-session navigation, the
  // sessionStorage copy after an OAuth redirect reload.
  const navState = (location.state ?? {}) as AuthReturn;
  const entryRef = useRef<AuthReturn>({ ...readStoredReturn(), ...navState });
  const entry = entryRef.current;
  useEffect(() => {
    if (navState.from || navState.ctaLabel) {
      sessionStorage.setItem(RETURN_KEY, JSON.stringify(entryRef.current));
    }
    // navState is a mount-time snapshot; this runs once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const funnelProps = {
    signup_intent: isSignupPage,
    cta_label: entry.ctaLabel,
    from: entry.from,
    path: location.pathname,
  };
  // Ref so effects/handlers always see current values without re-running.
  const funnelPropsRef = useRef(funnelProps);
  funnelPropsRef.current = funnelProps;

  // The email form defaults to the route's mode; the sign-up fallback for an
  // existing account can flip it to sign-in without a route change.
  const [isSignUp, setIsSignUp] = useState(isSignupPage);
  // /signup and /signin render this same component at the same tree
  // position, so a mode switch re-renders in place rather than remounting —
  // without this sync the form would keep the previous route's mode (the
  // "sign in instead" nudge showed the create-account form). Typed email and
  // password survive the switch on purpose; the mode-specific bits reset.
  useEffect(() => {
    setIsSignUp(isSignupPage);
    setError('');
    setShowReset(false);
    setResetSent(false);
    setConfirmPassword('');
  }, [isSignupPage]);
  const [showReset, setShowReset] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [isSigningIn, setIsSigningIn] = useState(false);
  const [showMoreProviders, setShowMoreProviders] = useState(false);
  // Read once per mount: the nudge must not appear mid-session as auth state
  // writes the flag.
  const [signedInBefore] = useState(deviceHasSignedIn);

  // Truthy (the provider name) while returning from an OAuth redirect. The
  // form is replaced with a "signing you in" state until auth settles: signed
  // in → the user effect below navigates into the app; not signed in (the
  // redirect was cancelled or failed) → the flag clears and the form shows.
  const [awaitingRedirect, setAwaitingRedirect] = useState<string | null>(() =>
    sessionStorage.getItem(REDIRECT_PENDING_KEY)
  );
  useEffect(() => {
    if (!awaitingRedirect || authLoading) return;
    sessionStorage.removeItem(REDIRECT_PENDING_KEY);
    if (!user) setAwaitingRedirect(null);
  }, [awaitingRedirect, authLoading, user]);

  // Funnel: one view event per mount. Skip when a signed-in user lands here —
  // they're redirected away below and never saw the page as a prospect — and
  // while a redirect return is settling (that view was already counted when
  // the redirect left; if it comes back cancelled, the flag clears and the
  // view is counted then).
  const viewTracked = useRef(false);
  useEffect(() => {
    if (viewTracked.current || user || awaitingRedirect) return;
    viewTracked.current = true;
    posthog?.capture(AnalyticsEvent.AuthPageViewed, { mode, ...funnelPropsRef.current });
  }, [user, mode, posthog, awaitingRedirect]);

  // Track signups completed via the OAuth redirect flow. The page reloaded on
  // the way back, so this effect is the first code that knows the outcome;
  // the module-scope guard keeps the conversion pixels to one fire per load.
  useEffect(() => {
    if (redirectResult?.isNewUser && user && !redirectSignupTracked) {
      redirectSignupTracked = true;
      trackSignUpReddit(user.uid);
      trackSignUpMeta(user.uid);
      posthog?.capture(AnalyticsEvent.SignUpCompleted, {
        method: 'oauth_redirect',
        ...funnelPropsRef.current,
      });
    }
  }, [redirectResult, user, posthog]);

  // Signed in (just now, or already when landing here): return to the page
  // that sent the user, falling back to home.
  useEffect(() => {
    if (!user) return;
    const to = entryRef.current.from ?? '/';
    sessionStorage.removeItem(RETURN_KEY);
    navigate(to, { replace: true });
  }, [user, navigate]);

  const switchMode = () => {
    navigate(isSignupPage ? '/signin' : '/signup', {
      replace: true,
      state: location.state,
    });
  };

  // Any swap to shorter content — the sign-in form after sign-up, the reset
  // view, the redirect-return state — would re-center the vertically-centered
  // grid and shrink the brand panel (the same class of jump FormErrorSlot
  // prevents). The column ratchets a minHeight to the tallest content it has
  // shown, so shorter views render inside the same frame. Desktop-only: the
  // held height is invisible beside the taller brand panel there, but in the
  // stacked mobile layout it becomes a blank gap above the panel (and mobile
  // has no vertical centering to protect anyway). A *width* resize clears it
  // so responsive reflow can't strand a stale height — height resizes are
  // ignored because mobile browsers fire them on every URL-bar show/hide.
  const isSideBySide = useMediaQuery('(min-width: 62em)');
  const formColRef = useRef<HTMLDivElement>(null);
  const [minColHeight, setMinColHeight] = useState<number | undefined>(undefined);
  // Re-measure on every view-level swap (mode, reset, redirect state,
  // linking error); within-view changes are height-neutral by design
  // (FormErrorSlot, the fixed more-options slot).
  useLayoutEffect(() => {
    if (!isSideBySide) return;
    const h = formColRef.current?.offsetHeight;
    if (h && (!minColHeight || h > minColHeight)) setMinColHeight(h);
  }, [isSideBySide, minColHeight, isSignUp, showReset, resetSent, awaitingRedirect, authError]);
  useEffect(() => {
    let lastWidth = window.innerWidth;
    const onResize = () => {
      if (window.innerWidth === lastWidth) return;
      lastWidth = window.innerWidth;
      setMinColHeight(undefined);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  const openReset = () => {
    setShowReset(true);
    setResetSent(false);
    setError('');
  };

  const handleProvider = async (provider: AuthProvider) => {
    posthog?.capture(AnalyticsEvent.AuthMethodSelected, {
      method: provider,
      ...funnelPropsRef.current,
    });
    setIsSigningIn(true);
    // The redirect flow leaves the page; the flag survives in sessionStorage
    // so the return renders the signing-you-in state instead of the form.
    if (!usesPopupAuth) sessionStorage.setItem(REDIRECT_PENDING_KEY, provider);
    try {
      const result = await signInWithProvider(provider);
      // Popup flow (localhost) resolves in place; the redirect flow resolves
      // in the redirect effect above after the round-trip.
      if (result?.isNewUser && result?.user) {
        trackSignUpReddit(result.user.uid);
        trackSignUpMeta(result.user.uid);
        posthog?.capture(AnalyticsEvent.SignUpCompleted, {
          method: provider,
          ...funnelPropsRef.current,
        });
      }
    } catch (error) {
      // The redirect never left the page (network error, provider refused);
      // without this a later visit would show the signing-you-in state.
      sessionStorage.removeItem(REDIRECT_PENDING_KEY);
      console.error('Sign-in failed:', error);
    } finally {
      setIsSigningIn(false);
    }
  };

  const handleEmailAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    posthog?.capture(AnalyticsEvent.AuthMethodSelected, {
      method: 'email',
      email_mode: isSignUp ? 'signup' : 'signin',
      ...funnelPropsRef.current,
    });
    setIsSigningIn(true);

    try {
      if (isSignUp) {
        if (password !== confirmPassword) {
          setError('Passwords do not match.');
          setIsSigningIn(false);
          return;
        }
        try {
          const result = await signUpWithEmail(email, password);
          if (result.isNewUser && result.user) {
            trackSignUpReddit(result.user.uid);
            trackSignUpMeta(result.user.uid);
            posthog?.capture(AnalyticsEvent.SignUpCompleted, {
              method: 'email',
              ...funnelPropsRef.current,
            });
          }
        } catch (signUpError: any) {
          if (signUpError.code !== 'auth/email-already-in-use') throw signUpError;

          // A returning user who took the sign-up path. If the password they
          // typed is their real one, this is the account they wanted, so sign
          // them in rather than making them notice their own mistake.
          try {
            await signInWithEmail(email, password);
          } catch {
            setIsSignUp(false);
            setConfirmPassword('');
            setError('You already have an account with this email. Enter your password to sign in.');
            return;
          }
        }
      } else {
        await signInWithEmail(email, password);
      }
      // Success: the user effect above navigates back to the referrer.
    } catch (error: any) {
      setError(error.message);
    } finally {
      setIsSigningIn(false);
    }
  };

  const handlePasswordReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsSigningIn(true);
    try {
      await resetPassword(email);
      setResetSent(true);
    } catch (error: any) {
      setError(error.message);
    } finally {
      setIsSigningIn(false);
    }
  };

  const primaryProviders = oauthProviders.filter((p) => !p.collapsed);
  const moreProviders = oauthProviders.filter((p) => p.collapsed);
  const awaitingProviderName = oauthProviders.find(
    (p) => p.provider === awaitingRedirect
  )?.name;

  return (
    <Box
      style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        background:
          'linear-gradient(135deg, var(--mantine-color-gray-0) 0%, rgba(255, 250, 245, 1) 100%)',
      }}
    >
      {/* Brand bar mirroring AppHeader's geometry exactly (56/60px tall,
          wordmark at px="md" from the viewport edge, vertically centered) so
          the wordmark doesn't shift when navigating between this page and
          pages wearing the app header. */}
      <Box
        component="header"
        h={{ base: 56, sm: 60 }}
        px="md"
        style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}
      >
        <UnstyledButton onClick={() => navigate('/')} aria-label="Meals You Love home">
          <BrandWordmark />
        </UnstyledButton>
      </Box>

      <Container
        size="lg"
        px="md"
        pb={{ base: 'md', md: 'xl' }}
        style={{ flex: 1, display: 'flex', flexDirection: 'column', width: '100%' }}
      >
        {/* Auto vertical margins center the content in the height left over
            below the brand bar on tall desktop displays; they collapse to
            zero when content fills the viewport (mobile, short windows),
            leaving just the symmetric padding. */}
        <Box my="auto" pt={{ base: 'lg', md: 40 }} pb={{ base: 0, md: 40 }}>
        <Grid gutter={{ base: 'xl', md: 48 }} align="stretch">
          {/* ── Form column ─────────────────────────────── */}
          <Grid.Col span={{ base: 12, md: 6 }} order={{ base: 1, md: 1 }}>
            <Box
              maw={440}
              mx="auto"
              ref={formColRef}
              style={{ minHeight: isSideBySide ? minColHeight : undefined }}
            >
              {awaitingRedirect ? (
                <Stack gap="md" align="center" ta="center" py={64}>
                  <Loader color="orange" size="lg" />
                  <Text fw={600} fz={24} style={{ fontFamily: heroFontFamily }}>
                    Signing you in{awaitingProviderName ? ` with ${awaitingProviderName}` : ''}…
                  </Text>
                  <Text size="sm" c="dimmed">
                    Just a moment while we connect your account.
                  </Text>
                </Stack>
              ) : showReset ? (
                <Stack gap="lg">
                  <Box>
                    <Text fw={600} fz={28} lh={1.2} style={{ fontFamily: heroFontFamily }}>
                      {resetSent ? 'Check your email' : 'Reset password'}
                    </Text>
                  </Box>
                  {resetSent ? (
                    <Stack gap="lg">
                      {/* Deliberately neutral: the endpoint won't say whether the
                          address has an account, so promising mail could be a lie. */}
                      <Text size="sm">
                        If an account exists for <strong>{email}</strong>, a reset link is on
                        its way. It expires in an hour.
                      </Text>
                      <Text size="xs" c="dimmed">
                        Nothing arriving? Check your spam folder, or go back and try another
                        address.
                      </Text>
                      <Button
                        variant="subtle"
                        size="xs"
                        style={{ alignSelf: 'center' }}
                        onClick={() => {
                          setResetSent(false);
                          setShowReset(false);
                          setError('');
                        }}
                      >
                        Back to sign in
                      </Button>
                    </Stack>
                  ) : (
                    <form onSubmit={handlePasswordReset}>
                      <Stack gap="lg">
                        <TextInput
                          label="Email"
                          type="email"
                          leftSection={<IconMail size={16} />}
                          value={email}
                          onChange={(e) => setEmail(e.currentTarget.value)}
                          required
                          withAsterisk={false}
                        />
                        {/* Reset errors are one-liners; the smaller slot keeps
                            the short form from reading as a hole */}
                        <FormErrorSlot error={error} mih={20} my={-8} />
                        <Button type="submit" loading={isSigningIn} fullWidth color="orange">
                          Send reset email
                        </Button>
                        <Button
                          variant="subtle"
                          size="xs"
                          style={{ alignSelf: 'center' }}
                          onClick={() => {
                            setShowReset(false);
                            setError('');
                          }}
                        >
                          Back to sign in
                        </Button>
                      </Stack>
                    </form>
                  )}
                </Stack>
              ) : (
                <Stack gap="md">
                  <Box>
                    <Text fw={600} fz={32} lh={1.15} style={{ fontFamily: heroFontFamily }}>
                      {isSignupPage ? 'Create your free account' : 'Welcome back'}
                    </Text>
                    <Text size="sm" c="dimmed" mt={6}>
                      {isSignupPage
                        ? '1-month free trial • No credit card required'
                        : 'Sign in to pick up where you left off.'}
                    </Text>
                  </Box>

                  {isSignupPage && signedInBefore && (
                    <Text size="sm" c="dimmed">
                      Looks like you've signed in here before —{' '}
                      <Anchor c="orange" fw={500} onClick={switchMode}>
                        sign in instead
                      </Anchor>
                    </Text>
                  )}

                  {authError && (
                    <Text size="sm" c="orange" fw={500}>{authError}</Text>
                  )}

                  <Stack gap="sm">
                    {primaryProviders.map(({ provider, name, icon }) => (
                      <Button
                        key={provider}
                        leftSection={
                          <ProviderLogo>
                            <div
                              style={{
                                display: 'flex',
                                filter: isSigningIn ? 'grayscale(100%) opacity(0.6)' : 'none',
                              }}
                            >
                              {icon}
                            </div>
                          </ProviderLogo>
                        }
                        variant="default"
                        size="md"
                        onClick={() => handleProvider(provider)}
                        disabled={isSigningIn}
                        fullWidth
                      >
                        Continue with {name}
                      </Button>
                    ))}
                    {/* Fixed-height slot: the link and the icon buttons it
                        reveals occupy the same 44px, so expanding never
                        changes the column height — which would re-center the
                        vertically-centered grid and stretch the brand panel,
                        shifting the whole page. */}
                    <Box
                      h={44}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      {!showMoreProviders ? (
                        <Anchor
                          size="xs"
                          c="dimmed"
                          onClick={() => setShowMoreProviders(true)}
                          style={{ cursor: 'pointer' }}
                        >
                          More sign-in options
                        </Anchor>
                      ) : (
                        <Group gap="sm">
                          {moreProviders.map(({ provider, name, icon }) => (
                            <Tooltip key={provider} label={`Continue with ${name}`}>
                              <ActionIcon
                                variant="default"
                                size={44}
                                radius="xl"
                                onClick={() => handleProvider(provider)}
                                disabled={isSigningIn}
                                aria-label={`Continue with ${name}`}
                              >
                                <ProviderLogo>
                                  <div
                                    style={{
                                      display: 'flex',
                                      filter: isSigningIn
                                        ? 'grayscale(100%) opacity(0.6)'
                                        : 'none',
                                    }}
                                  >
                                    {icon}
                                  </div>
                                </ProviderLogo>
                              </ActionIcon>
                            </Tooltip>
                          ))}
                        </Group>
                      )}
                    </Box>
                  </Stack>

                  <Divider label="or" labelPosition="center" />

                  <form onSubmit={handleEmailAuth}>
                    <Stack gap="md">
                      <TextInput
                        label="Email"
                        type="email"
                        placeholder="you@example.com"
                        leftSection={<IconMail size={16} />}
                        value={email}
                        onChange={(e) => setEmail(e.currentTarget.value)}
                        required
                        withAsterisk={false}
                      />
                      <PasswordInput
                        label="Password"
                        leftSection={
                          <IconLock
                            size={16}
                            color={
                              isSignUp && password
                                ? password.length >= 6
                                  ? 'var(--mantine-color-green-6)'
                                  : 'var(--mantine-color-red-6)'
                                : undefined
                            }
                          />
                        }
                        value={password}
                        onChange={(e) => setPassword(e.currentTarget.value)}
                        required
                        withAsterisk={false}
                      />
                      {isSignUp && (
                        <PasswordInput
                          label="Confirm password"
                          leftSection={
                            <IconLock
                              size={16}
                              color={
                                confirmPassword
                                  ? password === confirmPassword
                                    ? 'var(--mantine-color-green-6)'
                                    : 'var(--mantine-color-red-6)'
                                  : undefined
                              }
                            />
                          }
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.currentTarget.value)}
                          required
                          withAsterisk={false}
                        />
                      )}
                      <FormErrorSlot error={error} />
                      <Button
                        type="submit"
                        loading={isSigningIn}
                        disabled={
                          !email ||
                          !password ||
                          (isSignUp && (password.length < 6 || password !== confirmPassword))
                        }
                        fullWidth
                        size="md"
                        variant="gradient"
                        gradient={{ from: 'orange', to: 'red', deg: 45 }}
                      >
                        {isSignUp ? 'Create free account' : 'Sign in'}
                      </Button>
                    </Stack>
                  </form>

                  <Stack gap={6} align="center">
                    {!isSignUp && (
                      <Anchor
                        size="sm"
                        c="dimmed"
                        onClick={openReset}
                      >
                        Forgot password?
                      </Anchor>
                    )}
                    <Text size="sm" c="dimmed">
                      {isSignupPage ? 'Already have an account?' : 'New to Meals You Love?'}{' '}
                      <Anchor c="orange" fw={500} onClick={switchMode}>
                        {isSignupPage ? 'Sign in' : 'Create your free account'}
                      </Anchor>
                    </Text>
                    <Text size="xs" c="dimmed" ta="center">
                      By continuing, you agree to our{' '}
                      <Anchor size="xs" onClick={() => navigate('/terms')}>Terms of Service</Anchor>{' '}
                      and <Anchor size="xs" onClick={() => navigate('/privacy')}>Privacy Policy</Anchor>.
                    </Text>
                  </Stack>
                </Stack>
              )}
            </Box>
          </Grid.Col>

          {/* ── Brand panel ─────────────────────────────── */}
          <Grid.Col span={{ base: 12, md: 6 }} order={{ base: 2, md: 2 }}>
            <Box
              p={{ base: 'xl', md: 40 }}
              h="100%"
              style={{
                borderRadius: 24,
                background:
                  'linear-gradient(135deg, var(--mantine-color-orange-6) 0%, var(--mantine-color-red-6) 100%)',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              {/* No brand mark here: the logo is itself an orange gradient
                  and dissolves into the panel's orange ground — every backing
                  treatment (white chip, white mask, glass tile) fought it.
                  The page's brand row carries the mark; the serif headline
                  leads the panel. */}
              <Stack gap="xl" my="auto">
                <Text
                  c="white"
                  fw={600}
                  fz={{ base: 24, md: 30 }}
                  lh={1.25}
                  style={{ fontFamily: heroFontFamily }}
                >
                  Meal plans built around your life.
                </Text>

                <Stack gap="md">
                  {valueProps.map(({ icon: Icon, text }) => (
                    <Group key={text} gap="sm" wrap="nowrap" align="flex-start">
                      <Icon
                        size={18}
                        color="rgba(255, 255, 255, 0.9)"
                        style={{ flexShrink: 0, marginTop: 2 }}
                      />
                      <Text c="rgba(255, 255, 255, 0.95)" size="md" lh={1.5}>
                        {text}
                      </Text>
                    </Group>
                  ))}
                </Stack>

                <Box
                  p="md"
                  style={{
                    borderRadius: 'var(--mantine-radius-lg)',
                    background: 'rgba(255, 255, 255, 0.14)',
                    border: '1px solid rgba(255, 255, 255, 0.25)',
                  }}
                >
                  <Stack gap="xs">
                    <Text c="white" size="sm" lh={1.55} fs="italic">
                      "I don't have to think about what to cook that's nutritious, meets my
                      dietary needs, and doesn't take hours in the kitchen. It has saved me so
                      much time and money."
                    </Text>
                    <Text c="rgba(255, 255, 255, 0.85)" size="sm" fw={600}>
                      — Jennifer C.
                    </Text>
                  </Stack>
                </Box>
              </Stack>
            </Box>
          </Grid.Col>
        </Grid>
        </Box>
      </Container>
    </Box>
  );
};
